// Pure helpers for the multi-day route filter (days x couriers).
// A waybill's day is derived from `arrival_time`, which is hours from the
// manifest start: day 1 covers 0..24h, day 2 covers 24..48h and so on.
// The backend also returns a ready `day` field; it is preferred when present.

const HOURS_PER_DAY = 24;

export
const emptyFilter = Object.freeze({
  days: [],
  courierIds: [],
});

export
const waybillDay = (wp) => {
  if (!wp)
    return null;
  if (Number.isInteger(wp.day))
    return wp.day;
  const arrival = Number(wp.arrival_time);
  if (wp.arrival_time === null || wp.arrival_time === undefined || Number.isNaN(arrival))
    return null;
  return Math.floor(arrival / HOURS_PER_DAY) + 1;
};

export
const isFilterActive = (filter) => Boolean(filter)
  && ((filter.days && filter.days.length > 0) || (filter.courierIds && filter.courierIds.length > 0));

// Builds the option lists shown in the panel from the raw waybill rows
// returned by MANIFEST_WAYBILL_POINTS. Only days that actually contain
// routed points are listed (weekends of a 28-day schedule stay hidden).
export
const collectFilterOptions = (waybillsRaw) => {
  const days = new Set();
  const couriers = new Map();

  for (const wp of waybillsRaw || []) {
    const day = waybillDay(wp);
    if (day !== null)
      days.add(day);
    if (wp.courier_id && !couriers.has(wp.courier_id))
      couriers.set(wp.courier_id, {
        id: wp.courier_id,
        name: (wp.courier && wp.courier.name) || String(wp.courier_id),
      });
  }

  return {
    days: [...days].sort((a, b) => a - b),
    couriers: [...couriers.values()].sort((a, b) => a.name.localeCompare(b.name, 'ru')),
  };
};

export
const matchesFilter = (filter, courierId, day) => {
  if (!isFilterActive(filter))
    return true;
  if (filter.courierIds.length && !filter.courierIds.includes(Number(courierId)))
    return false;
  if (filter.days.length && !filter.days.includes(day))
    return false;
  return true;
};

// Applies the filter to the structures LeafletMap keeps in its state:
//   waybills:  { [courierId]: [{ pointId, day, ... }] }
//   mapPoints: { [pointId]: { id, depot, garage, ... } }
// Returns new objects; when the filter is inactive the inputs are returned
// untouched so downstream components keep their reference equality.
export
const applyRouteFilter = (filter, { waybills, mapPoints }) => {
  if (!isFilterActive(filter))
    return { waybills, mapPoints };

  const filteredWaybills = {};
  const visiblePointIds = new Set();

  for (const courierId of Object.keys(waybills || {})) {
    const entries = waybills[courierId].filter((wp) => matchesFilter(filter, courierId, wp.day));
    if (!entries.length)
      continue;
    filteredWaybills[courierId] = entries;
    for (const wp of entries)
      visiblePointIds.add(wp.pointId);
  }

  const filteredPoints = {};
  for (const p of Object.values(mapPoints || {})) {
    if (p.depot || p.garage || visiblePointIds.has(p.id))
      filteredPoints[p.id] = p;
  }

  return { waybills: filteredWaybills, mapPoints: filteredPoints };
};

export
const toggleInList = (list, value) => (list.includes(value)
  ? list.filter((v) => v !== value)
  : [...list, value]);
